import { type FocusEvent, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Toast } from './toast';
import { clearToasts, getSnapshot, subscribe } from './toast-manager';

interface ToasterProps {
  /** Names this stack, so `toast(…, { toaster: id })` raises into it rather than the app root's. */
  id?: string;
  /**
   * Where the region mounts. Pass a dialog element for a stack that has to work over that dialog.
   * A transform on that dialog makes it the containing block, placing the stack against it rather
   * than the viewport.
   */
  container?: HTMLElement;
}

/**
 * Mount this once at the app root to render whatever `toast()` raises. F8 moves focus to the newest
 * toast, and Escape puts it back where it was.
 *
 * A toast raised over a modal dialog needs its own `Toaster`, mounted inside that dialog and named:
 * `showModal()` makes everything outside it inert, top layer included.
 */
const Toaster = ({ id, container }: ToasterProps) => {
  const toasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const regionRef = useRef<HTMLOutputElement | null>(null);
  const returnTo = useRef<HTMLElement | undefined>(undefined);
  const target = container ?? document.body;
  const floating = target === document.body;

  const openRegion = useCallback(
    (region: HTMLOutputElement | null) => {
      regionRef.current = region;

      /* The top layer is an enhancement: without the Popover API the region is still a fixed box
         at the corner, it just sits under anything else the top layer holds. */
      if (region && floating) {
        region.togglePopover?.(true);
      }
    },
    [floating]
  );

  const newestToast = useCallback(
    () => regionRef.current?.querySelector<HTMLElement>('[data-slot="toast"]:not([data-state="closed"])'),
    []
  );

  const handleFocus = useCallback((event: FocusEvent<HTMLElement>) => {
    const { relatedTarget } = event;

    if (!event.currentTarget.contains(relatedTarget)) {
      returnTo.current = relatedTarget instanceof HTMLElement ? relatedTarget : undefined;
    }
  }, []);

  const returnFocus = useCallback(() => {
    const target = returnTo.current;

    returnTo.current = undefined;
    target?.focus();
  }, []);

  const handleReleaseFocus = useCallback(() => {
    const next = newestToast();

    if (next) {
      next.focus();

      return;
    }

    returnFocus();
  }, [newestToast, returnFocus]);

  useEffect(() => () => clearToasts(id), [id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' && event.key !== 'F8') {
        return;
      }

      const region = regionRef.current;

      if (!region || event.defaultPrevented) {
        return;
      }

      if (event.key === 'Escape') {
        const active = document.activeElement;

        if (!region.contains(active)) {
          return;
        }

        returnFocus();

        /* Blurred where focus did not land anywhere else, since leaving is the whole point, and
           taken rather than passed on to whatever else Escape would have closed. */
        const stranded = document.activeElement;

        if (region.contains(stranded) && stranded instanceof HTMLElement) {
          stranded.blur();
        }

        event.preventDefault();

        return;
      }

      newestToast()?.focus();

      if (!region.contains(document.activeElement)) {
        return;
      }

      event.preventDefault();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [newestToast, returnFocus]);

  /* Newest first, so tabbing into the stack reaches the newest toast's action before the older ones. */
  const stack = toasts.filter((record) => record.toaster === id).reverse();

  return createPortal(
    <output
      ref={openRegion}
      popover={floating ? 'manual' : undefined}
      aria-atomic="false"
      data-slot="toaster"
      onFocus={handleFocus}
    >
      {stack.map((toast) => (
        <Toast key={toast.id} toast={toast} onReleaseFocus={handleReleaseFocus} />
      ))}
    </output>,
    target
  );
};

export { Toaster, type ToasterProps };
